import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  ISupplierRepository,
  Supplier,
  SupplierFilters,
  CreateSupplierInput,
  UpdateSupplierInput,
} from '../../../interfaces/src';
import { consumeEntityCode, peekEntityCode } from './entity-codes';
import { fetchAllRows } from './fetch-all-rows';

const AUTHORIZED_SUPPLIERS = 'authorized_suppliers' as 'suppliers';

export class SupabaseSupplierRepository implements ISupplierRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async peekNextCode(): Promise<string> {
    return peekEntityCode(this.supabase, 'supplier');
  }

  async list(filters?: SupplierFilters): Promise<Supplier[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = this.supabase.from(AUTHORIZED_SUPPLIERS).select('*');
      if (filters?.search) {
        query = query.or(orContainsAnyColumn(['name', 'code'], filters.search));
      }
      if (filters?.type) query = query.eq('type', filters.type);
      return query
        .order('name', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to);
    });
    return rows.map(mapSupplier);
  }

  async getById(id: string): Promise<Supplier | null> {
    const { data, error } = await this.supabase
      .from(AUTHORIZED_SUPPLIERS)
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapSupplier(data);
  }

  async create(input: CreateSupplierInput): Promise<Supplier> {
    const code =
      input.code && input.code.trim() !== ''
        ? input.code.trim()
        : await consumeEntityCode(this.supabase, 'supplier');
    const { data, error } = await this.supabase
      .from('suppliers')
      .insert({
        code,
        name: input.name,
        type: input.type,
        tax_id: input.taxId,
        address: input.address,
        city: input.city,
        phone: input.phone,
        fax: input.fax,
        email: input.email,
        notes: input.notes,
      })
      .select('id')
      .single();
    if (error) throw error;
    const created = await this.getById(data.id);
    if (!created) throw new Error('supplier_read_after_create_denied');
    return created;
  }

  async update(id: string, input: UpdateSupplierInput): Promise<Supplier> {
    const mapped: TablesUpdate<'suppliers'> = {};
    if (input.code !== undefined) mapped['code'] = input.code;
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.type !== undefined) mapped['type'] = input.type;
    if (input.taxId !== undefined) mapped['tax_id'] = input.taxId;
    if (input.address !== undefined) mapped['address'] = input.address;
    if (input.city !== undefined) mapped['city'] = input.city;
    if (input.phone !== undefined) mapped['phone'] = input.phone;
    if (input.fax !== undefined)
      (mapped as Record<string, unknown>)['fax'] = input.fax;
    if (input.email !== undefined) mapped['email'] = input.email;
    if (input.notes !== undefined) mapped['notes'] = input.notes;

    const { data, error } = await this.supabase
      .from('suppliers')
      .update(mapped)
      .eq('id', id)
      .select('id')
      .single();
    if (error) throw error;
    const updated = await this.getById(data.id);
    if (!updated) throw new Error('supplier_read_after_update_denied');
    return updated;
  }

  async delete(id: string): Promise<void> {
    // Guard against deleting a supplier still referenced by purchase
    // documents or payments: throw a clear, mappable error (the global
    // toast maps a "foreign key" message to toasts.referenced) instead of
    // letting the delete fail opaquely or orphan rows.
    const { count: docCount, error: docErr } = await this.supabase
      .from('purchase_documents')
      .select('id', { count: 'exact', head: true })
      .eq('supplier_id', id);
    if (docErr) throw docErr;
    if ((docCount ?? 0) > 0) {
      throw new Error(
        'Cannot delete supplier: referenced by purchase documents (foreign key)',
      );
    }

    const { count: payCount, error: payErr } = await this.supabase
      .from('payments')
      .select('id', { count: 'exact', head: true })
      .eq('third_party_type', 'supplier')
      .eq('third_party_id', id);
    if (payErr) throw payErr;
    if ((payCount ?? 0) > 0) {
      throw new Error(
        'Cannot delete supplier: referenced by payments (foreign key)',
      );
    }

    const { error } = await this.supabase
      .from('suppliers')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }
}

function mapSupplier(row: Record<string, unknown>): Supplier {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    type: row['type'] as Supplier['type'],
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
